import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ModuleAccess from './ModuleAccess';

// Admin → Roles: who may approve, review, see or be notified. Replaces the hard-coded mailbox
// sets (multi-tenant, owner 2026-10-03). The catalogue of roles is the product's; the members are
// this tenant's. A member is a login (picked from Employees) or a plain mailbox; a person with two
// logins is grouped by a person key, and 'notify' marks which address receives emails.

type Spec = { scope: 'workspace' | 'company'; members: 'users' | 'mailboxes'; ordered?: boolean; label: string; hint: string };
type Member = { id?: string; company_id?: string; user_id: string; email: string; name: string; person_key: string; notify: number; sort_order: number };
type UserRow = { id: string; email: string; name: string; active?: number };

const input: React.CSSProperties = { padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 16, marginBottom: 12 };

// A company-scoped role's members live per company (company_id '' = the whole workspace, which a company
// without its own members uses); the card edits ONE company's list at a time (the approval circle is set
// per company, in order — Q15 2026-10-03).
function RoleCard({ roleKey, spec, members, users, companies, onSaved }: { roleKey: string; spec: Spec; members: Member[]; users: UserRow[]; companies: { id: string; name: string }[]; onSaved: () => void }) {
  const { t } = useTranslation();
  const scoped = spec.scope === 'company';
  const [companyId, setCompanyId] = useState(() => (!scoped || members.some(m => !m.company_id) ? '' : (members[0]?.company_id || '')));
  const membersOf = (co: string) => (scoped ? members.filter(m => (m.company_id || '') === co) : members);
  const [list, setList] = useState<Member[]>(() => membersOf(companyId));
  const [pickUser, setPickUser] = useState(''); const [pickMail, setPickMail] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  useEffect(() => { setList(membersOf(companyId)); }, [members, companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const countOf = (co: string) => new Set(members.filter(m => (m.company_id || '') === co).map(m => m.person_key || m.email)).size;
  const add = () => {
    if (spec.members === 'users' && pickUser) {
      const u = users.find(x => x.id === pickUser); if (!u) return;
      setList(l => [...l, { user_id: u.id, email: u.email, name: u.name, person_key: '', notify: 1, sort_order: spec.ordered ? l.length + 1 : 0 }]); setPickUser('');
    } else if (pickMail.trim()) {
      setList(l => [...l, { user_id: '', email: pickMail.trim().toLowerCase(), name: '', person_key: '', notify: 1, sort_order: spec.ordered ? l.length + 1 : 0 }]); setPickMail('');
    }
  };
  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      await api.adminSaveRole(roleKey, { companyId: scoped ? companyId : '', members: list.map((m, i) => ({ userId: m.user_id, email: m.email, personKey: m.person_key, notify: m.notify !== 0, sortOrder: spec.ordered ? (m.sort_order || i + 1) : 0 })) });
      setMsg(t('cs_saved')); setTimeout(() => setMsg(''), 2000); onSaved();
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{spec.label}</div>
        <code style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{roleKey}</code>
        {scoped && (
          <select value={companyId} onChange={e => { setCompanyId(e.target.value); setErr(''); setMsg(''); }} style={{ ...input, padding: '4px 8px', fontSize: 12 }}>
            <option value="">{t('roles_scope_workspace')}{countOf('') ? ` (${countOf('')})` : ''}</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}{countOf(c.id) ? ` (${countOf(c.id)})` : ''}</option>)}
          </select>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '2px 0 10px' }}>{spec.hint}</div>
      {/* A company with no members of its own uses the whole-workspace members — when there are any. */}
      {list.length === 0 && <div style={{ fontSize: 12.5, color: '#B45309', marginBottom: 8 }}>{scoped && companyId && countOf('') ? t('roles_company_inherits') : t('roles_none')}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {list.map((m, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            {spec.ordered && <input type="number" min={1} value={m.sort_order || i + 1} onChange={e => setList(l => l.map((x, j) => j === i ? { ...x, sort_order: Number(e.target.value) } : x))} style={{ ...input, width: 54, padding: '4px 6px' }} title={scoped ? t('roles_order') : t('roles_stage')} />}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name ? `${m.name} · ` : ''}{m.email}{m.user_id ? '' : ` (${t('roles_mailbox')})`}</span>
            <input value={m.person_key} placeholder={t('roles_person_key')} onChange={e => setList(l => l.map((x, j) => j === i ? { ...x, person_key: e.target.value } : x))} style={{ ...input, width: 110, padding: '4px 6px', fontSize: 12 }} title={t('roles_person_key_hint')} />
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }} title={t('roles_notify_hint')}>
              <input type="checkbox" checked={m.notify !== 0} onChange={e => setList(l => l.map((x, j) => j === i ? { ...x, notify: e.target.checked ? 1 : 0 } : x))} />{t('roles_notify')}
            </label>
            <button onClick={() => setList(l => l.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }} title={t('delete')}><i className="ti ti-x" /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {spec.members === 'users' ? (
          <select value={pickUser} onChange={e => setPickUser(e.target.value)} style={{ ...input, minWidth: 240 }}>
            <option value="">{t('roles_add_user')}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name ? `${u.name} · ` : ''}{u.email}</option>)}
          </select>
        ) : (
          <input value={pickMail} onChange={e => setPickMail(e.target.value)} placeholder={t('roles_add_mailbox')} onKeyDown={e => e.key === 'Enter' && add()} style={{ ...input, minWidth: 240 }} />
        )}
        <button onClick={add} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}><i className="ti ti-plus" /></button>
        <div style={{ flex: 1 }} />
        {err && <span style={{ fontSize: 12, color: '#E24B4A' }}>{err}</span>}
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" /> {msg}</span>}
        <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );
}

export default function Roles() {
  const { t } = useTranslation();
  const [data, setData] = useState<{ catalogue: Record<string, Spec>; members: Record<string, Member[]> } | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [err, setErr] = useState('');
  const load = () => {
    api.adminRoles().then(setData).catch((e: any) => setErr(e.message));
    api.adminGetUsers().then((d: any) => setUsers((Array.isArray(d) ? d : d?.users || []).filter((u: any) => u.active !== 0))).catch(() => {});
    api.adminGetCompanies().then((d: any) => setCompanies((Array.isArray(d) ? d : d?.companies || []).map((c: any) => ({ id: c.id, name: c.name })))).catch(() => {});
  };
  useEffect(load, []);
  if (err) return <div style={{ padding: 20, color: '#E24B4A' }}>{err}</div>;
  if (!data) return <div style={{ padding: 20, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <ModuleAccess />   {/* which modules are admin-only / owner-only (services/modulePolicy.ts) */}
      <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('roles_intro')}</div>
      {Object.entries(data.catalogue).map(([key, spec]) => {
        const rows = Object.entries(data.members).filter(([k]) => k.startsWith(key + ':')).flatMap(([k, v]) => v.map(m => ({ ...m, company_id: k.slice(key.length + 1) })));
        return <RoleCard key={key} roleKey={key} spec={spec} members={rows as any} users={users} companies={companies} onSaved={load} />;
      })}
    </div>
  );
}
